// Enquiries — staff-only follow-up tracker (Phase 1). List (oldest-at-risk first) with filters,
// a detail panel (enquirer details, status/priority controls, multi-assignee picker, append-only
// action timeline), and the §3.5 log-follow-up box that REQUIRES an outcome and then either marks
// Resolved (+ explanation) or sets a next-follow-up date. Internal tool; students/clients never
// see it (staff-gated route + server requireRole).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import { C, F, card, Btn, ErrorText } from '../components/admin/adminUi';
import AppHeader from '../components/AppHeader';
import PersonCombobox, { type PersonOption } from '../components/ops-board/PersonCombobox';
import { useIsMobile } from '../lib/useIsMobile';
import * as api from '../lib/enquiries';

const STATUS_LABELS: Record<api.EnquiryStatus, string> = {
  new: 'New', in_progress: 'In progress', awaiting_followup: 'Awaiting follow-up', resolved: 'Resolved', closed: 'Closed',
};
const STATUS_ORDER: api.EnquiryStatus[] = ['new', 'in_progress', 'awaiting_followup', 'resolved', 'closed'];

const inputStyle: CSSProperties = { fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, background: '#fff', color: C.text };
const fullInput: CSSProperties = { ...inputStyle, width: '100%' };
const labelStyle: CSSProperties = { fontWeight: 600, fontSize: 12, color: C.textDim, marginBottom: 3, display: 'block' };

function fmt(iso: string | null): string {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)); }
  catch { return iso.slice(0, 16).replace('T', ' '); }
}
// Allocated-staff label: "CODE - First Last" (structured name falls back to full_name; codeless
// staff show just the name — never a dangling " - "). Mirrors staffLabel for an assignee/creator
// PersonRef. Staff only — student/client displays keep their own personDisplayLabel handling.
function personRefLabel(p: api.PersonRef): string {
  const namePart = [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || p.full_name;
  const code = p.pilot_code?.trim();
  return code ? `${code} - ${namePart}` : namePart;
}

// "{pilot_code} - {first} {last}" (e.g. RAMMM - Max Ramm); structured name falls back to full_name
// (pre-Phase-3 rows); codeless staff show just the name — never a dangling " - ".
function staffLabel(s: api.StaffOption): string {
  const namePart = [s.first_name, s.last_name].filter(Boolean).join(' ').trim() || s.full_name;
  const code = s.pilot_code?.trim();
  return code ? `${code} - ${namePart}` : namePart;
}

// Timeline attribution label: "(CODE)" from the actor's pilot_code, else "(First Last)" / full name;
// a null actor is a system-generated entry → "(System)".
function actionActorLabel(a: api.EnquiryAction): string {
  if (!a.actor) return '(System)';
  const code = a.actor.pilot_code?.trim();
  if (code) return `(${code})`;
  const namePart = [a.actor.first_name, a.actor.last_name].filter(Boolean).join(' ').trim() || a.actor.full_name;
  return `(${namePart})`;
}

function Pill({ text, bg, fg }: { text: string; bg: string; fg: string }) {
  return <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' }}>{text}</span>;
}

interface FilterOption { value: string; label: string }

// Multi-select filter: a button showing the selected count that opens a checkbox popover with
// Select all / Clear. Selecting nothing = no filter (caller treats [] as "show all"). Optional
// search box filters the option labels (used for the assignee list, which can be long).
function FilterMultiSelect({ label, options, selected, onChange, searchable = false }: {
  label: string; options: FilterOption[]; selected: string[]; onChange: (next: string[]) => void; searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const needle = q.trim().toLowerCase();
  const shown = searchable && needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const btnStyle: CSSProperties = {
    ...inputStyle, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
    borderColor: selected.length ? C.accent : C.borderMed, color: selected.length ? C.text : C.textDim,
  };
  const rowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', fontSize: 13, color: C.text, cursor: 'pointer', borderRadius: 5 };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={btnStyle}>
        {label}{selected.length ? ` (${selected.length})` : ''} <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 30, top: 'calc(100% + 4px)', left: 0, minWidth: 220, maxWidth: 320,
          background: '#fff', border: `1px solid ${C.borderMed}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 8,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <button type="button" onClick={() => onChange(options.map((o) => o.value))} style={{ ...inputStyle, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}>Select all</button>
            <button type="button" onClick={() => onChange([])} style={{ ...inputStyle, padding: '3px 8px', fontSize: 12, cursor: 'pointer' }}>Clear</button>
          </div>
          {searchable && (
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" style={{ ...fullInput, marginBottom: 6 }} />
          )}
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            {shown.length === 0 && <div style={{ padding: 6, fontSize: 12, color: C.textMuted }}>No matches.</div>}
            {shown.map((o) => (
              <label key={o.value} style={rowStyle}>
                <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} style={{ accentColor: C.accent }} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Enquiries() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState<api.EnquiryListItem[]>([]);
  const [staff, setStaff] = useState<api.StaffOption[]>([]);
  const [bases, setBases] = useState<api.BaseRef[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // PR-000052: the enquiry you most recently opened, kept AFTER the detail is closed so the
  // list still shows where you were up to. Never cleared by the mobile Back button.
  const [lastViewedId, setLastViewedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<api.EnquiryDetail | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState('');

  // Multi-select filters. Empty array = show all for that facet. `?assignee=me` (e.g. from the
  // homepage badge) pre-selects the "assigned to me" view via the 'me' shortcut option.
  const [searchParams] = useSearchParams();
  const [fStatuses, setFStatuses] = useState<string[]>([]);
  const [fAssignees, setFAssignees] = useState<string[]>(searchParams.get('assignee') === 'me' ? ['me'] : []);
  const [fBases, setFBases] = useState<string[]>([]);
  const [fCreators, setFCreators] = useState<string[]>([]);
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [fOverdue, setFOverdue] = useState(false);
  const [fDue, setFDue] = useState(false);

  // Keyword search — debounced 300ms so typing doesn't fire a request per keystroke.
  const [search, setSearch] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setQDebounced(search), 300); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    const list = await api.listEnquiries({
      statuses: fStatuses.length ? (fStatuses as api.EnquiryStatus[]) : undefined,
      assignees: fAssignees.length ? fAssignees : undefined,
      baseIds: fBases.length ? fBases : undefined,
      q: qDebounced.trim() || undefined,
      creators: fCreators.length ? fCreators : undefined,
      created_from: fFrom || undefined,
      created_to: fTo || undefined,
      tz: (fFrom || fTo) ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
      overdue: fOverdue || undefined,
      due_for_followup: fDue || undefined,
    });
    setItems(list);
  }, [fStatuses, fAssignees, fBases, qDebounced, fCreators, fFrom, fTo, fOverdue, fDue]);

  const statusOptions = useMemo<FilterOption[]>(() => STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABELS[s] })), []);
  const assigneeOptions = useMemo<FilterOption[]>(
    () => [{ value: 'me', label: 'Assigned to me' }, ...staff.map((s) => ({ value: s.id, label: staffLabel(s) }))],
    [staff],
  );
  // Same staff list; the 'me' shortcut reads "Created by me" for the creator/assignor facet.
  const creatorOptions = useMemo<FilterOption[]>(
    () => [{ value: 'me', label: 'Created by me' }, ...staff.map((s) => ({ value: s.id, label: staffLabel(s) }))],
    [staff],
  );
  const baseOptions = useMemo<FilterOption[]>(
    () => [{ value: 'none', label: 'Unassigned' }, ...bases.map((b) => ({ value: b.id, label: b.name }))],
    [bases],
  );

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.fetchStaff().then(setStaff); api.fetchBases().then(setBases); }, []);

  // PR-000047: bring the detail into view on select — on MOBILE the detail replaces the list, so
  // it would otherwise render off-screen and the user has to scroll back up. Scroll ONLY when the
  // detail's top is outside the viewport: scrollIntoView is NOT a no-op on an already-visible
  // target (it still aligns to block start). setTimeout(0) runs after React commits the selection,
  // so the mobile detail column exists before we measure.
  // PR-000052: DESKTOP no longer scrolls at all. The detail column is sticky (below), so it is
  // always in view, and the old scroll yanked the page back up to it, carrying the row you had
  // just clicked out of the viewport — the exact "I lose my place" report.
  const detailRef = useRef<HTMLDivElement>(null);
  // openDetail must keep a STABLE identity: the deep-link effect below depends on it, so making
  // it depend on isMobile would re-run that effect (and re-open the enquiry) on every breakpoint
  // change. Read the current breakpoint from a ref instead.
  const isMobileRef = useRef(isMobile);
  useEffect(() => { isMobileRef.current = isMobile; }, [isMobile]);
  // PR-000052: where the LIST was scrolled to when a detail was opened on mobile, restored on Back.
  // Captured BEFORE the state update, because committing the selection hides the list and collapses
  // the document height, which the browser resolves by clamping window.scrollY.
  const listScrollRef = useRef<number | null>(null);
  const openDetail = useCallback(async (id: string) => {
    const mobile = isMobileRef.current;
    if (mobile) listScrollRef.current = window.scrollY;
    setSelectedId(id);
    setLastViewedId(id);
    if (mobile) {
      window.setTimeout(() => {
        const el = detailRef.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        if (top < 0 || top >= window.innerHeight) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
    setDetail(await api.getEnquiry(id));
  }, []);
  const refreshDetail = useCallback(async () => { if (selectedId) setDetail(await api.getEnquiry(selectedId)); }, [selectedId]);

  // Deep-link: /enquiries/:id (e.g. from a notification) opens that enquiry's detail on load.
  const { id: routeId } = useParams();
  useEffect(() => { if (routeId) void openDetail(routeId); }, [routeId, openDetail]);

  // PR-000052: the sticky detail must pin BELOW the sticky AppHeader (position: sticky, top: 0,
  // z-index 100), or its first rows sit behind it. The header height is MEASURED, not hardcoded:
  // it is a different bar on mobile and its desktop rows wrap at narrower widths, so any constant
  // would be wrong somewhere. Re-measured on resize, which is the only thing that changes it.
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const measure = () => setHeaderH(document.querySelector('header')?.getBoundingClientRect().height ?? 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const stickyTop = headerH + 12;

  // PR-000052: mobile Back restores the list to where it was. useLayoutEffect runs after the list
  // is back in the DOM but BEFORE paint, so there is no visible flash at the top first. The ref is
  // only ever set on mobile, so desktop never enters this path.
  useLayoutEffect(() => {
    if (selectedId !== null || listScrollRef.current === null) return;
    const y = listScrollRef.current;
    listScrollRef.current = null;
    window.scrollTo(0, y);
  }, [selectedId]);

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: F }}>
      <AppHeader context="Enquiries">
        <Link to="/" style={{ color: '#9fb4c7', fontSize: 13, textDecoration: 'none' }}>← Home</Link>
      </AppHeader>
      <main style={{ padding: '20px 18px 60px', maxWidth: 1240, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 18, margin: 0 }}>Enquiries</h1>
          <div style={{ marginLeft: 'auto' }}><Btn kind="primary" onClick={() => { setErr(''); setShowNew(true); }}>+ New enquiry</Btn></div>
        </div>

        {/* Filters — keyword search + each facet is a checkbox multi-select; empty = show all. */}
        <div style={{ ...card, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
          <div><label style={labelStyle}>Search</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email, phone, subject…"
              style={{ ...inputStyle, minWidth: 220 }} />
          </div>
          <div><label style={labelStyle}>Status</label>
            <FilterMultiSelect label="Status" options={statusOptions} selected={fStatuses} onChange={setFStatuses} />
          </div>
          <div><label style={labelStyle}>Assignee</label>
            <FilterMultiSelect label="Assignee" options={assigneeOptions} selected={fAssignees} onChange={setFAssignees} searchable />
          </div>
          <div><label style={labelStyle}>Creator</label>
            <FilterMultiSelect label="Creator" options={creatorOptions} selected={fCreators} onChange={setFCreators} searchable />
          </div>
          <div><label style={labelStyle}>Base</label>
            <FilterMultiSelect label="Base" options={baseOptions} selected={fBases} onChange={setFBases} />
          </div>
          <div><label style={labelStyle}>From</label>
            <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} style={inputStyle} />
          </div>
          <div><label style={labelStyle}>To</label>
            <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} style={inputStyle} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.textDim }}>
            <input type="checkbox" checked={fOverdue} onChange={(e) => setFOverdue(e.target.checked)} style={{ accentColor: C.accent }} /> Overdue
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.textDim }}>
            <input type="checkbox" checked={fDue} onChange={(e) => setFDue(e.target.checked)} style={{ accentColor: C.accent }} /> Due for follow-up
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 1fr) minmax(360px, 1.3fr)', gap: 16, alignItems: 'start' }}>
          {/* List — on mobile, hidden once an enquiry is selected (the detail takes over). */}
          {(!isMobile || !selectedId) && (
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {items.length === 0 && <div style={{ padding: 18, color: C.textMuted, fontSize: 13 }}>No enquiries match.</div>}
            {items.map((e) => (
              <button key={e.id} onClick={() => openDetail(e.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: `1px solid ${C.border}`,
                  padding: '11px 14px', cursor: 'pointer', fontFamily: F,
                  // Active selection keeps the existing grey. A row that was last opened but is no
                  // longer selected (mobile, after Back) gets the soft accent tint plus a left bar, so
                  // "where I was up to" survives closing the detail (PR-000052). The bar is an INSET
                  // shadow, not a border, so the row does not shift by 3px when it lights up.
                  background: e.id === selectedId ? C.pill : e.id === lastViewedId ? C.accentLight : '#fff',
                  boxShadow: e.id !== selectedId && e.id === lastViewedId ? `inset 3px 0 0 ${C.accent}` : undefined,
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13.5 }}>{e.enquirer_name || e.enquirer_company || e.enquirer_phone || 'Unnamed enquirer'}</strong>
                  {e.priority === 'urgent' && <Pill text="URGENT" bg={C.redLight} fg={C.red} />}
                  {e.overdue && <Pill text={`OVERDUE${e.escalation_level ? ` ·L${e.escalation_level}` : ''}`} bg={C.amberLight} fg={C.amber} />}
                  {e.due_for_followup && <Pill text="DUE" bg={C.amberLight} fg={C.amber} />}
                  {e.needs_base && <Pill text="NEEDS BASE" bg={C.accentLight} fg={C.accent} />}
                  <span style={{ marginLeft: 'auto' }}><Pill text={STATUS_LABELS[e.status]} bg={C.pill} fg={C.textMuted} /></span>
                </div>
                <div style={{ fontSize: 12.5, color: C.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject || e.body}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 3 }}>
                  Received {fmt(e.received_at)}{e.next_followup_at ? ` · next ${fmt(e.next_followup_at)}` : ''}{e.base ? ` · ${e.base.short_code}` : ''}
                </div>
              </button>
            ))}
          </div>
          )}

          {/* Detail — on mobile this is the whole view once an enquiry is picked, with a back link. */}
          {(!isMobile || selectedId) && (
          <div ref={detailRef} style={isMobile ? { scrollMarginTop: 12 } : {
            // PR-000052 desktop: the detail stays put while the list scrolls past it. The grid
            // already sets alignItems: 'start', so the grid AREA is the full row height and the
            // item can slide within it. A detail taller than the viewport scrolls INSIDE itself
            // (maxHeight + overflowY) rather than running off the bottom, which sticky cannot reach.
            position: 'sticky', top: stickyTop, maxHeight: `calc(100vh - ${stickyTop + 12}px)`,
            overflowY: 'auto', scrollMarginTop: 12,
          }}>
            {isMobile && selectedId && (
              <button onClick={() => { setSelectedId(null); setDetail(null); }}
                style={{ ...inputStyle, cursor: 'pointer', marginBottom: 10, background: '#fff', color: C.accent, fontWeight: 700 }}>← Back to list</button>
            )}
            {detail
              ? <Detail key={detail.id} detail={detail} staff={staff} bases={bases}
                  onChanged={() => { refreshDetail(); load(); }} />
              : <div style={{ ...card, color: C.textMuted, fontSize: 13 }}>{isMobile && selectedId ? 'Loading…' : 'Select an enquiry to view its details and log a follow-up.'}</div>}
          </div>
          )}
        </div>
      </main>

      {showNew && (
        <NewEnquiry bases={bases} onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); load(); openDetail(id); }} setErr={setErr} err={err} />
      )}
    </div>
  );
}

// ─── Detail panel ────────────────────────────────────────────────────────────────────
function Detail({ detail, staff, bases, onChanged }: { detail: api.EnquiryDetail; staff: api.StaffOption[]; bases: api.BaseRef[]; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Log-follow-up box state (PR-000045 disposition flow: every entry is either "closed" or
  // auto-defers, optionally with a reminder).
  const [outcome, setOutcome] = useState('');
  const [actionType, setActionType] = useState<api.ActionInput['action_type']>('contact_made');
  const [closed, setClosed] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [remind, setRemind] = useState(false);
  const [followUp, setFollowUp] = useState('');

  const assignedIds = new Set(detail.assignees.map((a) => a.user_id));
  const pickerOptions: PersonOption[] = useMemo(
    () =>
      staff.filter((s) => !assignedIds.has(s.id)).map((s) => ({
        id: s.id,
        label: staffLabel(s),
        code: s.pilot_code,
        first_name: s.first_name,
        last_name: s.last_name,
        full_name: s.full_name,
      })),
    [staff, detail.assignees],
  );

  async function changeStatus(status: api.EnquiryStatus) {
    setBusy(true); setErr('');
    const r = await api.patchEnquiry(detail.id, { status });
    setBusy(false);
    if (!r.ok) setErr(r.error || 'Update failed.'); else onChanged();
  }
  async function changePriority(priority: api.EnquiryPriority) {
    setBusy(true); setErr('');
    const r = await api.patchEnquiry(detail.id, { priority });
    setBusy(false);
    if (!r.ok) setErr(r.error || 'Update failed.'); else onChanged();
  }
  async function changeBase(base_id: string) {
    setBusy(true); setErr('');
    const r = await api.patchEnquiry(detail.id, { base_id: base_id || null });
    setBusy(false);
    if (!r.ok) setErr(r.error || 'Update failed.'); else onChanged();
  }
  async function addAssignee(userId: string) {
    setBusy(true); setErr('');
    const r = await api.addAssignee(detail.id, userId);
    setBusy(false);
    if (!r.ok) setErr(r.error || 'Could not assign.'); else onChanged();
  }
  async function removeAssignee(userId: string) {
    setBusy(true); setErr('');
    const r = await api.removeAssignee(detail.id, userId);
    setBusy(false);
    if (!r.ok) setErr(r.error || 'Could not unassign.'); else onChanged();
  }

  // Display default for the reminder time: 24 hours from now, as a datetime-local value. The
  // backend applies its own authoritative default when no explicit time is sent.
  function defaultFollowUpLocal(): string {
    const d = new Date(Date.now() + 24 * 3_600_000);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  }

  async function submitAction() {
    setErr('');
    if (!outcome.trim()) { setErr('An outcome is required.'); return; }
    const input: api.ActionInput = { action_type: actionType, outcome: outcome.trim() };
    if (closed) {
      input.resolved = true;
      if (explanation.trim()) input.body = explanation.trim();
    } else if (remind) {
      if (!followUp) { setErr('Choose a time for the reminder.'); return; }
      input.follow_up_at = new Date(followUp).toISOString();
      input.reminder_enabled = true;
    }
    // Not closed and no reminder: send neither field; the server schedules the next follow-up
    // automatically at its default interval.
    setBusy(true);
    const r = await api.logAction(detail.id, input);
    setBusy(false);
    if (!r.ok) { setErr(r.error || 'Could not log the follow-up.'); return; }
    setOutcome(''); setExplanation(''); setFollowUp(''); setClosed(false); setRemind(false); setActionType('contact_made');
    onChanged();
  }

  return (
    <div style={{ ...card }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 16, margin: 0 }}>{detail.enquirer_name || detail.enquirer_company || 'Enquiry'}</h2>
        {detail.priority === 'urgent' && <Pill text="URGENT" bg={C.redLight} fg={C.red} />}
        {detail.overdue && <Pill text={`OVERDUE${detail.escalation_level ? ` · L${detail.escalation_level}` : ''}`} bg={C.amberLight} fg={C.amber} />}
      </div>
      <div style={{ fontSize: 12.5, color: C.textDim, margin: '6px 0 12px' }}>
        {[detail.enquirer_phone, detail.enquirer_email, detail.enquirer_company].filter(Boolean).join(' · ') || 'No contact details'}
      </div>
      {detail.subject && <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 4 }}>{detail.subject}</div>}
      <div style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap', background: C.pill, borderRadius: 8, padding: 10, marginBottom: 14 }}>{detail.body}</div>

      {/* Controls */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
        <div><label style={labelStyle}>Status</label>
          <select value={detail.status} disabled={busy} onChange={(e) => changeStatus(e.target.value as api.EnquiryStatus)} style={fullInput}>
            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <div><label style={labelStyle}>Priority</label>
          <select value={detail.priority} disabled={busy} onChange={(e) => changePriority(e.target.value as api.EnquiryPriority)} style={fullInput}>
            <option value="normal">Normal</option><option value="urgent">Urgent</option>
          </select>
        </div>
        <div><label style={labelStyle}>Base</label>
          <select value={detail.base_id ?? ''} disabled={busy} onChange={(e) => changeBase(e.target.value)} style={fullInput}>
            <option value="">— none —</option>
            {bases.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          {/* Triage hint (PR-000053): the caller's own words, so the base is one click away.
              Server-derived; disappears once a base is set. */}
          {detail.caller_location && (
            <div style={{ fontSize: 11.5, color: C.accent, marginTop: 4, wordBreak: 'break-word' }}>
              Caller gave location: {detail.caller_location}
            </div>
          )}
        </div>
      </div>

      {/* Assignees */}
      <label style={labelStyle}>Assignees</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {detail.assignees.length === 0 && <span style={{ fontSize: 12.5, color: C.textMuted }}>Nobody assigned yet.</span>}
        {detail.assignees.map((a) => (
          <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, background: C.pill, borderRadius: 999, padding: '3px 8px' }}>
            {personRefLabel(a.user)}
            <button onClick={() => removeAssignee(a.user_id)} disabled={busy} title="Remove" style={{ border: 'none', background: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 14, lineHeight: 1 }}>×</button>
          </span>
        ))}
      </div>
      <div style={{ maxWidth: 320, marginBottom: 16 }}>
        <PersonCombobox placeholder="Add an assignee…" options={pickerOptions} onSelect={addAssignee} />
      </div>

      {/* Log follow-up (the §3.5 forcing function) */}
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginBottom: 14 }}>
        <h3 style={{ fontSize: 13.5, margin: '0 0 8px' }}>Log a follow-up</h3>
        <div style={{ display: 'flex', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 160 }}><label style={labelStyle}>Action</label>
            <select value={actionType} onChange={(e) => setActionType(e.target.value as api.ActionInput['action_type'])} style={fullInput}>
              <option value="contact_made">Contact made</option>
              <option value="contact_attempt">Contact attempted</option>
              <option value="outcome">Outcome update</option>
              <option value="note">Note</option>
            </select>
          </div>
        </div>
        <label style={labelStyle}>Outcome <span style={{ color: C.red }}>*</span></label>
        <textarea value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="What happened? (e.g. left voicemail, starting August, not interested)"
          style={{ ...fullInput, minHeight: 54, resize: 'vertical', marginBottom: 8 }} />

        {/* PR-000045 disposition: every entry is either closed or auto-defers (+ optional reminder). */}
        <label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, color: C.text, marginBottom: 4, cursor: 'pointer' }}>
          <input type="checkbox" checked={closed} onChange={(e) => { setClosed(e.target.checked); if (e.target.checked) setRemind(false); }} style={{ accentColor: C.accent }} />
          Consider this enquiry closed?
        </label>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: C.amber, marginBottom: 8 }}>
          Do not mark this closed unless you actually reached the client. If you only left a message, leave it open.
        </div>

        {closed ? (
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle}>Explanation (optional)</label>
            <textarea value={explanation} onChange={(e) => setExplanation(e.target.value)} placeholder="How was it resolved?" style={{ ...fullInput, minHeight: 44, resize: 'vertical' }} />
          </div>
        ) : (
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, color: C.text, marginBottom: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={remind} onChange={(e) => { setRemind(e.target.checked); if (e.target.checked && !followUp) setFollowUp(defaultFollowUpLocal()); }} style={{ accentColor: C.accent }} />
              Would you like a reminder to follow this up?
            </label>
            {remind && (
              <div style={{ marginBottom: 6 }}>
                <label style={labelStyle}>Remind me at</label>
                <input type="datetime-local" value={followUp} onChange={(e) => setFollowUp(e.target.value)} style={fullInput} />
              </div>
            )}
            <div style={{ fontSize: 11.5, color: C.textMuted }}>
              {remind
                ? 'You will get a notification and an email when this falls due. Escalation pauses until then.'
                : 'The next follow-up is scheduled automatically for 24 hours after this entry. Escalation pauses until then.'}
            </div>
          </div>
        )}

        <Btn kind="primary" disabled={busy} onClick={submitAction}>Log follow-up</Btn>
        <ErrorText>{err}</ErrorText>
      </div>

      {/* Timeline */}
      <h3 style={{ fontSize: 13.5, margin: '0 0 8px' }}>Activity</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {detail.actions.length === 0 && <span style={{ fontSize: 12.5, color: C.textMuted }}>No activity yet.</span>}
        {[...detail.actions].reverse().map((a) => {
          // Attribution prefix: actor's pilot_code → "(SKINB)", else "(First Last)"; system → "(System)".
          const label = actionActorLabel(a);
          // Prefix the FIRST visible content line only (outcome takes precedence over body).
          const onOutcome = !!a.outcome;
          return (
            <div key={a.id} style={{ fontSize: 12.5, borderLeft: `3px solid ${a.action_type === 'escalation' || a.action_type === 'followup_reminder' ? C.amber : C.border}`, paddingLeft: 10 }}>
              <div style={{ color: C.textMuted, fontSize: 11 }}>{a.action_type.replace(/_/g, ' ')} · {fmt(a.created_at)}{a.actor_id ? '' : ' · system'}</div>
              {a.outcome && <div style={{ color: C.text }}><strong>{label}:</strong> {a.outcome}</div>}
              {a.body && <div style={{ color: C.textDim }}>{onOutcome ? a.body : <><strong>{label}:</strong> {a.body}</>}</div>}
              {!a.outcome && !a.body && <div style={{ color: C.textDim }}><strong>{label}</strong></div>}
              {a.follow_up_at && <div style={{ color: C.textMuted, fontSize: 11 }}>Next follow-up: {fmt(a.follow_up_at)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── New enquiry modal ──────────────────────────────────────────────────────────────────
function NewEnquiry({ bases, onClose, onCreated, setErr, err }: { bases: api.BaseRef[]; onClose: () => void; onCreated: (id: string) => void; setErr: (s: string) => void; err: string }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<api.EnquiryPriority>('normal');
  const [baseId, setBaseId] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    setErr('');
    if (!body.trim()) { setErr('A message is required.'); return; }
    setBusy(true);
    const r = await api.createEnquiry({
      enquirer_name: name.trim() || null, enquirer_phone: phone.trim() || null,
      enquirer_email: email.trim() || null, enquirer_company: company.trim() || null,
      subject: subject.trim() || null, body: body.trim(), priority, base_id: baseId || null,
    });
    setBusy(false);
    if (!r.ok || !r.id) { setErr(r.error || 'Could not create the enquiry.'); return; }
    onCreated(r.id);
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: 560, maxWidth: '100%', fontFamily: F, color: C.text }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>New enquiry</h2>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 20, color: C.textMuted, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>Name</label><input value={name} onChange={(e) => setName(e.target.value)} style={fullInput} /></div>
          <div><label style={labelStyle}>Phone</label><input value={phone} onChange={(e) => setPhone(e.target.value)} style={fullInput} /></div>
          <div><label style={labelStyle}>Email</label><input value={email} onChange={(e) => setEmail(e.target.value)} style={fullInput} /></div>
          <div><label style={labelStyle}>Company</label><input value={company} onChange={(e) => setCompany(e.target.value)} style={fullInput} /></div>
          <div><label style={labelStyle}>Priority</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value as api.EnquiryPriority)} style={fullInput}>
              <option value="normal">Normal</option><option value="urgent">Urgent</option>
            </select>
          </div>
          <div><label style={labelStyle}>Base</label>
            <select value={baseId} onChange={(e) => setBaseId(e.target.value)} style={fullInput}>
              <option value="">— none —</option>
              {bases.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 10 }}><label style={labelStyle}>Subject</label><input value={subject} onChange={(e) => setSubject(e.target.value)} style={fullInput} /></div>
        <div style={{ marginTop: 10 }}><label style={labelStyle}>Message <span style={{ color: C.red }}>*</span></label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} style={{ ...fullInput, minHeight: 80, resize: 'vertical' }} /></div>
        <ErrorText>{err}</ErrorText>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" disabled={busy} onClick={submit}>Create enquiry</Btn>
        </div>
      </div>
    </div>
  );
}
